import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, ButtonBase, Collapse, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { slotDisplayName } from '../photo-upload/PhotoUpload'
import { imageUrl } from './reviewModel'

const THUMB = 72

// Another shot of the same piece, sent to Gemini beside this one so it can
// see details this angle hides (a stone setting, an engraving). Its original
// capture, not its edit: that is what the server sends. Controlled: the piece
// owns the choice, so Re-roll and Edit prompt both send it.
export default function DetailReference({ slot, slots, selectedId, onChange, disabled, defaultOpen = false }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(defaultOpen)
    const others = slots.filter(other => other.photoId !== slot.photoId && other.hasOriginal)
    if (others.length === 0) return null

    const chosen = others.find(other => other.photoId === selectedId)
    const preview = other => imageUrl(other.photoId, 'raw', other.rawVersion, 'preview')

    return (
        <Box sx={{ width: '100%' }}>
            <Button
                size='small'
                aria-expanded={open}
                onClick={() => setOpen(prev => !prev)}
                // The chosen shot, small, on the toggle: what rides along is
                // visible before Re-roll even with the strip closed.
                startIcon={chosen && (
                    <Box component='img' src={preview(chosen)} alt='' sx={{ width: 20, height: 20, objectFit: 'cover', borderRadius: 0.5, display: 'block' }} />
                )}
            >
                {chosen ? t('photoReview.detailRef.chosen', { label: slotDisplayName(t, chosen.slot) }) : t('photoReview.detailRef.toggle')}
            </Button>
            <Collapse in={open} unmountOnExit>
                <Typography variant='caption' color='text.secondary' component='p' sx={{ mt: 0.5, mb: 1 }}>
                    {t('photoReview.detailRef.hint')}
                </Typography>
                <Box component='ul' aria-label={t('photoReview.detailRef.listLabel')}
                    sx={{ listStyle: 'none', m: 0, p: 0, pb: 0.5, pt: 0.5, pl: 0.5, display: 'flex', gap: 1.5, overflowX: 'auto' }}>
                    {others.map(other => {
                        const label = slotDisplayName(t, other.slot)
                        const isChosen = other.photoId === selectedId
                        return (
                            <Box component='li' key={other.photoId} sx={{ flex: '0 0 auto', width: THUMB }}>
                                <ButtonBase
                                    aria-label={label}
                                    aria-pressed={isChosen}
                                    disabled={disabled}
                                    onClick={() => onChange(isChosen ? null : other.photoId)}
                                    sx={{
                                        position: 'relative', display: 'block', width: THUMB, height: THUMB, borderRadius: 1, overflow: 'hidden',
                                        outline: theme => (isChosen ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`),
                                        outlineOffset: isChosen ? 2 : 0,
                                        opacity: disabled ? 0.5 : 1,
                                        '&.Mui-focusVisible': { outline: theme => `3px solid ${theme.palette.primary.dark}` },
                                    }}
                                >
                                    <img
                                        src={preview(other)}
                                        alt={t('photoReview.detailRef.alt', { label })}
                                        loading='lazy'
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    />
                                    {isChosen && (
                                        <CheckCircleIcon color='primary' fontSize='small'
                                            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', borderRadius: '50%' }} />
                                    )}
                                </ButtonBase>
                                <Typography variant='caption' component='div' sx={{ mt: 0.5, fontWeight: isChosen ? 600 : 400, lineHeight: 1.3 }}>
                                    {label}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>
                {chosen && (
                    <Button size='small' sx={{ mt: 0.5 }} disabled={disabled} onClick={() => onChange(null)}>
                        {t('photoReview.detailRef.clear')}
                    </Button>
                )}
            </Collapse>
        </Box>
    )
}
